import { CortexSetting, LocationVarKey } from "@swivl-mono/types";

import { CortexSettingRow } from "../../Components/CortexSettingRow";
import { TopicCard } from "../../Components/TopicCard";
import { useTopicRow } from "../../Hooks/useTopicContext";

/**
 * Tiered welcome messages (ENG-3115, spec `docs/specs/tiered-welcome-messages.md`).
 * One toggle, then a tier × channel grid of verbatim openings. Every cell is optional —
 * an empty cell means the bot's normal greeting applies for that tier on that channel.
 */

type Tier = "KnownCustomer" | "ReturningUser" | "Unknown";
type Channel = "Web" | "Sms" | "Voice";

const TIERS: { key: Tier; label: string; who: string }[] = [
  { key: "KnownCustomer", label: "Known customer", who: "has an active lease" },
  { key: "ReturningUser", label: "Returning user", who: "has talked to us before, no active lease" },
  { key: "Unknown", label: "Unknown", who: "no records of them" }
];

const CHANNELS: { key: Channel; label: string; note?: string }[] = [
  { key: "Web", label: "Web chat" },
  { key: "Sms", label: "SMS", note: "On SMS this is the reply to the person's first text; their question is answered on the next turn." },
  { key: "Voice", label: "Voice" }
];

const ENABLED_SETTING: CortexSetting = {
  kind: "toggle",
  key: "welcomeMessagesEnabled",
  varKey: LocationVarKey.CortexWelcomeMessagesEnabled,
  label: "Use custom welcome messages",
  description:
    "When ON, the messages below open the conversation exactly as written. Cells left empty fall back to the bot's normal greeting. A conversation opened by a Conversation Starter or a widget CTA never shows a welcome message.",
  default: false
};

function cellVarKey(tier: Tier, channel: Channel): LocationVarKey {
  const key = LocationVarKey[`CortexWelcomeMessage${tier}${channel}` as keyof typeof LocationVarKey];
  // A stale tier name here resolved to `undefined` and every cell for that tier silently read as
  // Default while the server held its value (2026-09-04 rename). Fail at module load instead.
  if (!key) throw new Error(`WelcomeMessages: no LocationVarKey for tier ${tier} / channel ${channel}`);
  return key;
}

function cellSetting(tier: (typeof TIERS)[number], channel: (typeof CHANNELS)[number]): CortexSetting {
  return {
    kind: "variable-textarea",
    key: `welcomeMessage${tier.key}${channel.key}`,
    varKey: cellVarKey(tier.key, channel.key),
    label: `${tier.label} — ${channel.label}`,
    description: `Sent on ${channel.label.toLowerCase()} to a ${tier.label.toLowerCase()} (${tier.who}).${channel.note ? ` ${channel.note}` : ""}`,
    placeholder: "Not set — the bot's normal greeting applies.",
    default: ""
  };
}

export const TOPIC_VARS: LocationVarKey[] = [
  LocationVarKey.CortexWelcomeMessagesEnabled,
  ...TIERS.flatMap((tier) => CHANNELS.map((channel) => cellVarKey(tier.key, channel.key)))
];

export function WelcomeMessagesTopic(): React.ReactElement {
  // The grid is only meaningful when the feature is on — hidden otherwise (Chuck, 2026-08-27).
  // Draft first so toggling ON reveals it immediately, before the save round-trip.
  const { merged, draft } = useTopicRow(LocationVarKey.CortexWelcomeMessagesEnabled);
  const enabled = (draft?.value ?? merged.value ?? "0") === "1";

  return (
    <TopicCard
      title="Welcome Messages"
      description="Your own opening line per kind of arriving person, per channel. Type {{ to insert a variable such as the location's name."
    >
      <CortexSettingRow setting={ENABLED_SETTING} layout="horizontal" />
      {!enabled && (
        <p className="body-sm px-4 pt-2 text-grey-600">
          Off — every conversation opens with the bot&apos;s normal greeting. Turn this on to write your own opening for each kind of arriving person.
        </p>
      )}
      {enabled &&
        TIERS.map((tier) => (
          <div key={tier.key} className="border-t border-grey-100 pt-3">
            <h4 className="heading-sm px-4 pb-1 text-grey-900">{tier.label}</h4>
            <p className="body-sm px-4 pb-2 text-grey-600">{tier.who}.</p>
            {CHANNELS.map((channel) => (
              <CortexSettingRow key={channel.key} setting={cellSetting(tier, channel)} />
            ))}
          </div>
        ))}
    </TopicCard>
  );
}
